import { memo, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight, FlipVertical2, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { useGameStore } from '../../store/game-store';
import { useSchermoSveglio } from '../../hooks/useSchermoSveglio';
import { useBlindGame } from './blind-context';
import './ReviewControls.css';

interface ReviewControlsProps {
  /** `bar` = compact icon group inside the mobile in-game bar; `panel` = sidebar
   *  header above the move table (also shows a position indicator). */
  variant?: 'bar' | 'panel';
  /** Content placed at the left of the bar (bar variant only): on mobile the
   *  "Moves" trigger sits here, before the prev/next/go-live navigation cluster. */
  centerSlot?: ReactNode;
  /**
   * «Questa e' la damiera d'ANALISI», e porta due conseguenze — tenute insieme da un flag
   * solo proprio perche' non devono poter divergere:
   *   - non c'e' nessuna partita viva a cui essere ritrascinati, e muovere da un ply
   *     rivisto E' il punto: il go-live non deve assillare;
   *   - la card resta resa anche a linea vuota, perche' ospita i comandi di come si GUARDA
   *     la posizione e quelli servono prima della prima mossa (vedi la guardia qui sotto).
   *
   * NON governa piu' «vai all'inizio»: quel bottone c'e' dappertutto, perche' tornare al
   * principio della linea serve in partita quanto in analisi.
   */
  analysisMode?: boolean;
  /**
   * «Gira la damiera», in fondo a destra. La rotazione la possiede il CHIAMANTE — qui
   * dentro non c'e' nessuno stato: la damiera d'analisi la combina con il lato di chi
   * muove, e un bottone che si tenesse il proprio booleano direbbe il contrario di lei.
   */
  onFlipBoard?: () => void;
  /** La rotazione corrente, per `aria-pressed` del bottone qui sopra. */
  boardFlipped?: boolean;
  /**
   * Quante semi-mosse ha messo il sorteggio: il PAVIMENTO di «vai all'inizio».
   *
   * L'inizio della partita e' la posizione DOPO l'apertura imposta, non la damiera
   * nuda: quelle semi-mosse non le ha giocate nessuno dei due.
   *
   * Arriva dal CHIAMANTE e non da `game-store.openingPlyCount`, che e' scritto solo
   * in locale/IA/presenza — `enterOnlineGame` e `syncFromExternal` non lo toccano,
   * quindi online/arena/torneo leggerebbero zero (o il residuo della partita prima).
   * La fonte giusta e' una per contesto (multiplayer-store, arena-store, l'apertura
   * della partita in torneo, `opening.moves.length` per chi guarda) e deve essere LA
   * STESSA che GameScreen passa alla barra mobile, o il bottone atterrerebbe in due
   * punti diversi secondo la larghezza dello schermo.
   */
  openingPlyCount?: number;
  /**
   * IL CURSORE e lo scorrimento automatico, su una seconda fila sotto le frecce.
   *
   * Solo la damiera d'ANALISI lo chiede: in partita e nello spettatore i comandi vivono in
   * una barra laterale stretta o nella barra del telefono, dove una seconda fila non c'e'
   * dove metterla. E' un asse a parte da `analysisMode` di proposito — quel flag tiene
   * insieme due conseguenze «perche' non devono poter divergere», e questa e' una terza
   * cosa, scorrelata: un domani il cursore puo' servire anche altrove.
   *
   * Lo stato dello scorrimento vive QUI e non in chi monta, e non e' un dettaglio: le
   * frecce devono poterlo SPEGNERE (vedi `vaiA`), e sono qui dentro. Sollevarlo renderebbe
   * quell'invariante remoto, cioe' aggirabile.
   */
  conCursore?: boolean;
  /**
   * IL CHEVRON che apre e chiude la tabella delle mosse, in coda alla fila del cursore.
   *
   * Tre prop che vanno INSIEME — il bottone c'e' solo se c'e' `onToggleMosse`, e senza le
   * altre due mentirebbe a chi naviga a voce. Lo stato lo possiede CHI MONTA, per la stessa
   * ragione scritta su `onFlipBoard`: la lista la rende l'overlay, e un bottone con un
   * booleano proprio direbbe il contrario di lei.
   *
   * `idMosse` e' l'id della regione comandata, e questo componente non sa altro di lei:
   * resta generico.
   */
  mosseAperte?: boolean;
  onToggleMosse?: () => void;
  idMosse?: string;
}

/**
 * Step through past moves (review) and return to the live position. Works in BOTH
 * AI and online modes by driving the store's mode-agnostic `reviewToIndex`:
 *   - index `0..total-1` shows a past position (board renders `reviewPieces`);
 *   - `null` / `>= total` is the live position.
 * Online review is read-only (the board stays non-interactive until "go live");
 * in AI mode the takeback-on-pickup flow is unaffected.
 */
function ReviewControls({
  variant = 'panel',
  centerSlot,
  analysisMode = false,
  onFlipBoard,
  boardFlipped = false,
  openingPlyCount = 0,
  conCursore = false,
  mosseAperte = false,
  onToggleMosse,
  idMosse,
}: ReviewControlsProps) {
  const { t } = useTranslation();
  const reviewIndex = useGameStore((s) => s.reviewIndex);
  const total = useGameStore((s) => s.moveHistory.length);
  const reviewToIndex = useGameStore((s) => s.reviewToIndex);
  const captureInProgress = useGameStore((s) => s.captureInProgress);
  const gameMode = useGameStore((s) => s.gameMode);
  const blind = useBlindGame();

  /* QUESTI TRE STANNO SOPRA I RITORNI ANTICIPATI, e non per gusto: li legge l'effetto
     dello scorrimento automatico qui sotto, e un hook non puo' stare sotto un `return`.
     Il resto delle derivazioni e' rimasto dov'era, dopo le guardie. */
  const currentIdx = reviewIndex ?? total; // live === total
  const blocked = captureInProgress !== null; // don't disrupt a multi-capture
  // L'INIZIO DELLA PARTITA, NON DELLA LISTA: `Math.min` perche' un conto piu' lungo
  // della linea non e' un indice, e li' «dopo l'apertura» E' il vivo (lo store rimappa
  // tutto cio' che e' >= total).
  const startIdx = Math.min(openingPlyCount, total);

  /** Scorrimento automatico in corso: una mossa al secondo (vedi l'effetto sotto). */
  const [autoplay, setAutoplay] = useState(false);

  /*
   * Lo schermo resta acceso durante lo SCORRIMENTO, non per tutta la revisione.
   *
   * Navigando a mano si tocca lo schermo a ogni mossa e il conto alla rovescia del
   * sistema si riarma da se': li' non c'e' niente da risolvere. E' lo scorrimento
   * automatico — una mossa al secondo e nessun tocco — ad avere il problema.
   *
   * Chiamato sempre, anche dove il cursore non c'e': l'hook esce al primo rigo quando
   * l'argomento e' falso, senza toccare il contatore di modulo, quindi non litiga col
   * lucchetto che la partita tiene gia' (ne' con l'altro ReviewControls che GameScreen
   * puo' montare insieme a questo).
   */
  useSchermoSveglio(autoplay);

  /*
   * Lo scorrimento automatico: una mossa al secondo.
   *
   * Un `setTimeout` per MOSSA e non un `setInterval`, perche' si riarma su ogni ply: si
   * spegne da se' quando arriva in fondo, e un salto a mano in mezzo riparte dal ply
   * nuovo. Lo schema e' quello di GameReplay, tradotto su `reviewToIndex`.
   *
   * Non passa da `vaiA` di proposito — quella e' la porta della navigazione A MANO e mette
   * in pausa: passarci lo spegnerebbe al primo scatto.
   */
  useEffect(() => {
    if (!autoplay) return;
    /* In fondo si spegne. E in mezzo a una presa multipla ANCHE: `reviewToIndex` e' un
       no-op finche' `captureInProgress` e' vivo, e senza questo ramo l'effetto non si
       riarmerebbe mai (le dipendenze non cambiano) — lo scorrimento resterebbe acceso a
       girare a vuoto, col bottone che mostra «pausa» per sempre. */
    if (blocked || currentIdx >= total) { setAutoplay(false); return; }
    const id = setTimeout(() => reviewToIndex(currentIdx + 1), 1000);
    return () => clearTimeout(id);
  }, [autoplay, blocked, currentIdx, total, reviewToIndex]);

  // ALLA CIECA LA REVISIONE NON ESISTE. La benda regge anche indietro nel tempo (vedi
  // GameScreen), quindi le frecce promettono una posizione che non si vedra' mai: sotto
  // la benda la posizione rivista e' vuota esattamente quanto quella corrente. E in una
  // partita online farebbero danno — entrare in revisione blocca la damiera, e l'unica
  // uscita era proprio il «vai alla posizione attuale» qui sotto.
  //
  // Vale per tutte e due le varianti: sparisce anche il contatore n/N della colonna
  // laterale. Chi rende la barra mobile non deve percio' passare `centerSlot` quando e'
  // bendato: il trigger «Mosse» e' ospitato qui dentro e se ne andrebbe insieme alle
  // frecce. Al suo posto GameScreen mette `BlindLastMoveButton`.
  if (blind) return null;

  // Nothing to review before any move is played — but when a centerSlot is hosted
  // (the mobile "Moves" trigger), keep rendering so that button stays available;
  // the arrows simply render disabled until the first move. In ANALISI vale la stessa
  // cosa per un'altra ragione: la card ospita «gira la damiera», e il momento in cui
  // serve di piu' e' proprio quello in cui la linea e' ancora vuota — si e' appena
  // arrivati dall'editor e si decide da che lato guardare.
  if (total === 0 && !centerSlot && !analysisMode) return null;

  const isReviewing = reviewIndex !== null;
  // Only human games (pvp/online) lock the board during review, so going live is
  // mandatory to resume play — blink the go-live button there. AI review is
  // non-blocking (pick-up triggers a takeback), so it must NOT blink. Nemmeno la
  // partita in presenza: li' la presa di un pezzo annulla, esattamente come in analisi.
  const mustGoLive = isReviewing && gameMode === 'pvp' && !analysisMode;
  const canPrev = currentIdx > 0 && !blocked;
  const canNext = isReviewing && !blocked; // forward only meaningful while reviewing
  // `!==` e non `>`: sotto l'apertura — ci si arriva con la freccia indietro, o con un
  // tocco sulle semi-mosse grigie della lista — il bottone porta AVANTI, fino all'inizio
  // vero. E' quello che promette l'etichetta, non «indietro tutta».
  const canGoToStart = !blocked && total > 0 && currentIdx !== startIdx;

  /**
   * LA PORTA DELLA NAVIGAZIONE A MANO, e mette in pausa lo scorrimento automatico.
   *
   * Frecce, vai-all'inizio, vai-al-vivo, trascinamento del cursore: chi tocca un comando
   * sta prendendo il timone. Lo scatto automatico NON passa da qui, percio' non si
   * auto-spegne — e' la stessa divisione che GameReplay fa fra `goTo` e il suo timer.
   *
   * Dove il cursore non c'e', `autoplay` e' sempre falso e `setAutoplay(false)` non fa
   * nulla: React non ri-rende su uno stato identico.
   */
  const vaiA = (i: number | null) => { setAutoplay(false); reviewToIndex(i); };

  /**
   * Avvia o ferma lo scorrimento automatico.
   *
   * In fondo riparte da capo: a chi ripreme play sull'ultima posizione serve rivedere la
   * linea, non restare fermo. Niente `vaiA` qui — rimetterebbe `autoplay` a false — e
   * niente `reviewToIndex` dentro l'updater di `setAutoplay`, che in StrictMode gira due
   * volte. Si riparte da `startIdx` e non da zero: e' lo stesso inizio del bottone
   * «vai all'inizio», cioe' la posizione dopo l'eventuale apertura imposta.
   */
  const scorrimento = () => {
    if (autoplay) { setAutoplay(false); return; }
    if (currentIdx >= total) reviewToIndex(startIdx);
    setAutoplay(true);
  };

  /* LE FRECCE, cioe' la prima fila. Estratte in una variabile perche' con il cursore
     vanno avvolte in una riga loro (vedi il ritorno): a fila sola restano figlie dirette
     della card, esattamente come prima.
     `centerSlot` NON e' qui dentro: e' roba della variante `bar`, che non si combina mai
     col cursore, e un test ne inchioda l'ordine rispetto alle frecce. */
  const frecce = (
    <>
      {/* bar variant: the "Moves" trigger sits at the left, before the arrows. */}
      {variant === 'bar' && centerSlot}

      {/* `reviewToIndex(startIdx)` NON e' «torna al vivo»: lo store tratta cosi' solo
          `null` e gli indici fuori scala. Lo zero ricostruisce da `startBoard` senza
          applicare nessuna mossa; `startIdx` ci rigioca sopra le sole semi-mosse
          imposte dal sorteggio — cioe' atterra dove comincia la partita vera.
          L'unico caso in cui questo bottone ESCE dalla revisione e' la partita in cui
          si e' giocata solo l'apertura: la' `startIdx === total`, e il post-apertura e'
          il vivo per davvero. */}
      <button
        type="button"
        className="review-controls-btn"
        data-testid="review-go-to-start"
        onClick={() => vaiA(startIdx)}
        disabled={!canGoToStart}
        aria-label={t('game.sidebar.goToStart', 'Go to start')}
        title={t('game.sidebar.goToStart', 'Go to start')}
      >
        <SkipBack size={18} aria-hidden="true" />
      </button>

      <button
        type="button"
        className="review-controls-btn"
        onClick={() => vaiA(currentIdx - 1)}
        disabled={!canPrev}
        aria-label={t('game.sidebar.prevMove', 'Previous move')}
        title={t('game.sidebar.prevMove', 'Previous move')}
      >
        <ChevronLeft size={18} aria-hidden="true" />
      </button>

      {variant === 'panel' && (
        <span className="review-controls-counter" aria-hidden="true">
          {currentIdx}/{total}
        </span>
      )}

      <button
        type="button"
        className="review-controls-btn"
        onClick={() => vaiA(currentIdx + 1)}
        disabled={!canNext}
        aria-label={t('game.sidebar.nextMove', 'Next move')}
        title={t('game.sidebar.nextMove', 'Next move')}
      >
        <ChevronRight size={18} aria-hidden="true" />
      </button>

      <button
        type="button"
        className={`review-controls-btn review-controls-live${isReviewing ? ' is-active' : ''}${mustGoLive ? ' is-warning' : ''}`}
        onClick={() => vaiA(null)}
        disabled={!isReviewing}
        aria-label={t('game.sidebar.goLive', 'Go to current position')}
        title={t('game.sidebar.goLive', 'Go to current position')}
      >
        <SkipForward size={18} aria-hidden="true" />
      </button>

      {/* NON `FlipBoardButton`, che pure esiste: le sue classi stanno in GameToolbar.css,
          foglio che la schermata d'analisi non carica — arriverebbe un bottone nudo (la
          stessa ragione per cui GameReplay ha il suo). Condiviso resta cio' che conta,
          l'etichetta: la stessa chiave del comando in partita. Mai disabilitato, nemmeno
          a meta' di una presa: girare la damiera non e' una mossa. */}
      {onFlipBoard && (
        <button
          type="button"
          className="review-controls-btn review-controls-flip"
          data-testid="analysis-flip"
          onClick={onFlipBoard}
          aria-pressed={boardFlipped}
          aria-label={t('game.actions.flipBoard', 'Flip board')}
          title={t('game.actions.flipBoard', 'Flip board')}
        >
          <FlipVertical2 size={18} aria-hidden="true" />
        </button>
      )}
    </>
  );

  return (
    <div
      className={`review-controls review-controls--${variant}${conCursore ? ' review-controls--cursore' : ''}`}
      data-testid="review-controls"
    >
      {/* A fila sola le frecce sono figlie dirette della card. Col cursore prendono una
          riga loro, e la card diventa una colonna di due: l'involucro c'e' SOLO in quella
          modalita', percio' niente cambia dove il cursore non c'e'. */}
      {conCursore ? <div className="review-controls-riga">{frecce}</div> : frecce}

      {conCursore && (
        /* LA SECONDA FILA: play/pausa a sinistra, poi il cursore.
           Il play sta qui e non in coda alle frecce perche' e' il cursore che mette in
           moto — e perche' la' il posto e' finito: a 360px le cinque frecce piu' il
           contatore prendono ~298px dei 328 dell'interno della card. */
        <div className="review-controls-cursore">
          <button
            type="button"
            className="review-controls-btn review-controls-play"
            data-testid="review-play"
            onClick={scorrimento}
            disabled={total === 0 || blocked}
            /* Due t() a chiave letterale e non un t(cond ? a : b): la guardia che
               controlla l'esistenza delle chiavi salta quelle non letterali. */
            title={autoplay ? t('game.sidebar.pause', 'Pause') : t('game.sidebar.play', 'Play')}
            aria-label={autoplay ? t('game.sidebar.pause', 'Pause') : t('game.sidebar.play', 'Play')}
            aria-pressed={autoplay}
          >
            {autoplay
              ? <Pause size={18} aria-hidden="true" />
              : <Play size={18} fill="currentColor" aria-hidden="true" />}
          </button>

          {/* L'input in una colonna sua: `flex: 1` con `min-width: 0`, cosi' il binario
              prende tutto l'avanzo senza strizzare il bottone accanto. */}
          <div className="review-controls-binario">
            <input
              type="range"
              min={0}
              max={total}
              value={currentIdx}
              disabled={total === 0 || blocked}
              /* Nessun clamp: lo store mappa da se' ogni indice `>= total` sul vivo,
                 quindi il fondo del binario E' la posizione corrente. */
              onChange={(e) => vaiA(parseInt(e.target.value, 10))}
              /* NON «Mossa successiva»: questo e' il cursore, non la freccia avanti. Con
                 la stessa etichetta del bottone accanto, chi naviga a voce si trovava due
                 comandi diversi che si chiamano uguale. */
              aria-label={t('game.sidebar.position', 'Position in the line')}
            />
          </div>

          {/* IL CHEVRON, in coda alla fila e non in testa: in testa sposterebbe il play, che
              un test tiene per primo figlio, e soprattutto il chevron punta in GIU' — verso
              la tabella che comanda, che sta sotto. La fila non ci rimette un pixel di
              binario che conti: a 360px i 328 dell'interno della card diventano 44 + 4 +
              binario + 4 + 44, cioe' 232 di cursore.
              Mai disabilitato, nemmeno a linea vuota o a meta' di una presa: riporre la
              tabella non e' una mossa (la stessa ragione scritta su `review-controls-flip`). */}
          {onToggleMosse && (
            <button
              type="button"
              className={`review-controls-btn review-controls-mosse${mosseAperte ? ' is-open' : ''}`}
              data-testid="analysis-moves-toggle"
              onClick={onToggleMosse}
              aria-expanded={mosseAperte}
              /* Solo mentre la regione esiste: un `aria-controls` che punta a un id
                 assente e' esso stesso un difetto. */
              aria-controls={mosseAperte ? idMosse : undefined}
              /* Due t() a chiave letterale e non un t(cond ? a : b): la guardia che
                 controlla l'esistenza delle chiavi salta quelle non letterali. */
              title={mosseAperte ? t('game.sidebar.hideMoves', 'Hide moves') : t('game.sidebar.showMoves', 'Show moves')}
              aria-label={mosseAperte ? t('game.sidebar.hideMoves', 'Hide moves') : t('game.sidebar.showMoves', 'Show moves')}
            >
              <ChevronDown size={18} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(ReviewControls);
